import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguageContext } from "@/contexts/LanguageContext";
import { isSupportedLanguage, SOURCE_LANGUAGE, LANGUAGE_CODES } from "@/config/languages";

const storageKey = (storeId: string) => `store_lang_${storeId}`;

/**
 * Loads a store's enabled + default languages (public read-only RPC) and keeps the
 * shopper's language inside that set. First visit => store default; later visits => saved choice.
 */
export function useStoreLanguages(storeId?: string | null) {
  // Storefront language is scoped: it never overwrites the platform language.
  const { currentLanguage, setStorefrontLanguage: setCurrentLanguage } = useLanguageContext();

  // Leaving the storefront restores the platform language.
  useEffect(() => () => setCurrentLanguage(null), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [enabled, setEnabled] = useState<string[]>([SOURCE_LANGUAGE]);
  const [defaultLanguage, setDefaultLanguage] = useState<string>(SOURCE_LANGUAGE);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!storeId) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await (supabase.rpc as any)("get_store_languages_public", { _store_id: storeId });
      const row = Array.isArray(data) ? data[0] : data;
      const def = isSupportedLanguage(row?.default_language) ? row.default_language : SOURCE_LANGUAGE;
      let list: string[] = (row?.enabled_languages ?? []).filter((c: string) => LANGUAGE_CODES.includes(c));
      if (error || !row) list = [def];
      if (!list.includes(def)) list = [def, ...list];
      if (cancelled) return;
      setEnabled(list);
      setDefaultLanguage(def);
      const saved = localStorage.getItem(storageKey(storeId));
      const target = saved && list.includes(saved) ? saved : def;
      setCurrentLanguage(target);
      setReady(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  // RTL for Arabic on the storefront
  useEffect(() => {
    document.documentElement.dir = currentLanguage === "ar" ? "rtl" : "ltr";
    return () => { document.documentElement.dir = "ltr"; };
  }, [currentLanguage]);

  const selectLanguage = (code: string) => {
    if (!enabled.includes(code)) return;
    if (storeId) localStorage.setItem(storageKey(storeId), code);
    setCurrentLanguage(code);
  };

  return { enabledLanguages: enabled, defaultLanguage, currentLanguage, selectLanguage, ready };
}
